import { forwardRef } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { formatRupees } from "@/lib/format";
import { isNumeric, looksLikeMoney, isTotalRow } from "./catalog";

const StatusPill = ({ value }) => {
  const t = value.trim().toUpperCase();
  const cls =
    t === "PAID" ? "bg-green-50 text-green-700 border-green-200" :
    t === "OPEN" || t === "PENDING" ? "bg-yellow-50 text-yellow-700 border-yellow-200" :
    t === "PARTIAL" ? "bg-orange-50 text-orange-700 border-orange-200" :
    t === "LATE" ? "bg-red-50 text-red-700 border-red-200" :
    t === "IN" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
    "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span className={`inline-flex text-[10px] font-medium tracking-wider uppercase px-1.5 py-0.5 rounded border ${cls}`}>
      {t}
    </span>
  );
};

const fmtCell = (v, colName) => {
  if (v === null || v === undefined || v === "") return <span className="text-slate-300">—</span>;
  if (isNumeric(v)) {
    const money = looksLikeMoney(colName);
    return <span className="font-mono-fin tabular-nums">{money ? formatRupees(v) : v.toLocaleString("en-IN")}</span>;
  }
  if (typeof v === "string" && /^(PAID|OPEN|PARTIAL|LATE|PENDING|IN|OUT)$/i.test(v.trim())) {
    return <StatusPill value={v} />;
  }
  return v;
};

/**
 * Data table with sticky header, sortable columns, freeze-first-column,
 * total-row emphasis, and empty-state.
 */
const ReportTable = forwardRef(function ReportTable(
  { rows, columns, analysis, sort, onToggleSort, freezeFirst, search, footerLeft, footerRight },
  ref
) {
  return (
    <div ref={ref} className="border border-slate-200 bg-white rounded-md overflow-hidden">
      <div className="overflow-x-auto max-h-[calc(100vh-320px)] thin-scroll">
        <table className="w-full text-[12.5px] border-separate border-spacing-0">
          <thead className="bg-slate-50 sticky top-0 z-20">
            <tr>
              {columns.map((c, colIdx) => {
                const isNum = analysis[c] !== undefined;
                const isActive = sort?.col === c;
                const isFrozen = freezeFirst && colIdx === 0;
                return (
                  <th
                    key={c}
                    onClick={() => onToggleSort(c)}
                    className={`p-3 text-[10px] tracking-wider uppercase text-slate-500 whitespace-nowrap cursor-pointer select-none hover:text-slate-900 border-b border-slate-200 ${
                      isNum ? "text-right" : "text-left"
                    } ${isFrozen ? "sticky left-0 z-30 bg-slate-50 shadow-[2px_0_0_0_#e2e8f0]" : ""}`}
                    data-testid={`col-${c}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {c}
                      {isActive && (sort.dir === "asc"
                        ? <ChevronUp className="h-3 w-3" />
                        : <ChevronDown className="h-3 w-3" />)}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              const isTotal = isTotalRow(r);
              const rowBg = isTotal ? "bg-red-50/60" : idx % 2 ? "bg-white" : "bg-slate-50/40";
              return (
                <tr
                  key={idx}
                  className={`${isTotal ? "total border-t-2 border-red-200 font-semibold" : "hover:bg-slate-100/60"} transition-colors`}
                >
                  {columns.map((c, colIdx) => {
                    const v = r[c];
                    const isNum = analysis[c] !== undefined;
                    const isFrozen = freezeFirst && colIdx === 0;
                    return (
                      <td
                        key={c}
                        className={`p-2.5 whitespace-nowrap border-b border-slate-100 ${rowBg} ${
                          isNum ? "text-right num" : "text-left text-slate-700"
                        } ${isFrozen ? "sticky left-0 z-10 shadow-[2px_0_0_0_#e2e8f0] font-medium" : ""}`}
                      >
                        {fmtCell(v, c)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="p-10 text-center text-slate-400 text-[12px]">
                  No rows match “{search}”.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between text-[10px] text-slate-500 flex-wrap gap-2">
        <span>{footerLeft}</span>
        <span>{footerRight}</span>
      </div>
    </div>
  );
});

export default ReportTable;
